import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { isTodayBirthday } from '../utils/ageCalculator';
import { loadAllUserBadges } from '../utils/badgeCalculator';
import BirthdayCelebration from './BirthdayCelebration';

interface PracticeSession {
  id: string;
  studentId: string;
  lessonId: string;
  plannedTotalMinutes: number;
  plannedDurations: Record<string, number>;
  actualDurations: Record<string, number>;
  completed: boolean;
  startAt: any;
  endAt?: any;
  durationSec?: number;
  taskCounts: Record<string, number>;
  createdAt: any;
}

const Awards: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);
  const [badgesLoaded, setBadgesLoaded] = useState(false);
  const [showBadgeCelebration, setShowBadgeCelebration] = useState(false);
  const [showBirthdayCelebration, setShowBirthdayCelebration] = useState(false);
  const [newBadge, setNewBadge] = useState<{ id: string; name: string; description: string; emoji: string; color: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Celebration messages
  const celebrationMessages = [
    "I Nailed My Practice!",
    "Practice Power Unlocked!",
    "Badge Achievement Unlocked!",
    "Amazing Work!",
    "You're on Fire!",
    "Practice Champion!",
    "Incredible Progress!",
    "You Did It!",
    "Outstanding Achievement!",
    "Practice Master!",
    "Well Deserved!",
    "Fantastic Work!",
    "You Rock!",
    "Practice Star!",
    "Consistency Wins Again!"
  ];

  // Birthday celebration messages
  const birthdayMessages = [
    "Happy Birthday Practice Star! 🎂",
    "Birthday Practice Champion! 🎉",
    "What dedication on your special day! 🎂",
    "Birthday Practice Hero! 🎁",
    "You rock on your birthday! 🎂🎵",
    "Birthday Practice Superstar! 🌟",
    "Amazing dedication on your day! 🎂",
    "Birthday Practice Legend! 🎊"
  ];

  // Badge definitions
  const badgeDefinitions: Record<string, { emoji: string; name: string; description: string; color: string; category: string }> = {
    // Original Streak Badges
    'first-day': { emoji: '🎯', name: 'First Steps', description: 'Completed your first practice session', color: 'from-blue-500 to-cyan-500', category: 'Streak' },
    'three-day-streak': { emoji: '🔥', name: 'Hot Streak', description: '3-day practice streak', color: 'from-orange-500 to-red-500', category: 'Streak' },
    'week-streak': { emoji: '⭐', name: 'Week Warrior', description: '7-day practice streak', color: 'from-yellow-500 to-orange-500', category: 'Streak' },
    'two-week-streak': { emoji: '💪', name: 'Consistency King', description: '14-day practice streak', color: 'from-green-500 to-emerald-500', category: 'Streak' },
    'month-streak': { emoji: '👑', name: 'Practice Legend', description: '30-day practice streak', color: 'from-purple-500 to-pink-500', category: 'Streak' },
    
    // Original Weekly Goals
    'first-week': { emoji: '📅', name: 'Week Complete', description: 'Met weekly goal once', color: 'from-indigo-500 to-blue-500', category: 'Weekly Goals' },
    'month-consistency': { emoji: '🏆', name: 'Monthly Master', description: '4 weeks of meeting goals', color: 'from-amber-500 to-yellow-500', category: 'Weekly Goals' },
    'quarter-master': { emoji: '🎖️', name: 'Quarter Champion', description: '12 weeks of meeting goals', color: 'from-rose-500 to-pink-500', category: 'Weekly Goals' },
    
    // Original Time Milestones
    'hour-master': { emoji: '⏰', name: 'Hour Hero', description: '1 hour total practice time', color: 'from-teal-500 to-cyan-500', category: 'Time Milestones' },
    'five-hour-club': { emoji: '🎵', name: 'Five Hour Club', description: '5 hours total practice time', color: 'from-violet-500 to-purple-500', category: 'Time Milestones' },
    'thousand-minute-master': { emoji: '💯', name: 'Thousand Minute Master', description: '1000 minutes total practice', color: 'from-emerald-500 to-green-500', category: 'Time Milestones' },
    'practice-legend': { emoji: '🌟', name: 'Practice Legend', description: '5000 minutes total practice', color: 'from-yellow-400 to-orange-500', category: 'Time Milestones' },
    
    // Original Consistency
    'focused-practicer': { emoji: '🎯', name: 'Focused Practicer', description: 'Average 30+ min sessions', color: 'from-blue-600 to-indigo-600', category: 'Consistency' },
    'dedicated-musician': { emoji: '🎼', name: 'Dedicated Musician', description: 'Average 60+ min sessions', color: 'from-purple-600 to-violet-600', category: 'Consistency' },
    
    // NEW: Learning & Growth
    'quick-learner': { emoji: '🚀', name: 'Quick Learner', description: 'Complete 7 practice sessions in your first week', color: 'from-green-400 to-blue-500', category: 'Learning & Growth' },
    'curious-mind': { emoji: '🧠', name: 'Curious Mind', description: 'Try 5 different practice categories in one session', color: 'from-purple-400 to-pink-500', category: 'Learning & Growth' },
    'explorer': { emoji: '🗺️', name: 'Explorer', description: 'Practice 10 different pieces/exercises', color: 'from-orange-400 to-red-500', category: 'Learning & Growth' },
    'technique-master': { emoji: '⚡', name: 'Technique Master', description: 'Focus on technique for 3 consecutive sessions', color: 'from-yellow-400 to-orange-500', category: 'Learning & Growth' },
    
    // NEW: Time Management
    'early-bird': { emoji: '🌅', name: 'Early Bird', description: 'Practice before 8 AM', color: 'from-yellow-300 to-orange-400', category: 'Time Management' },
    'night-owl': { emoji: '🦉', name: 'Night Owl', description: 'Practice after 8 PM', color: 'from-indigo-600 to-purple-700', category: 'Time Management' },
    'weekend-warrior': { emoji: '⚔️', name: 'Weekend Warrior', description: 'Complete practice on both weekend days', color: 'from-green-500 to-teal-600', category: 'Time Management' },
    'marathon-session': { emoji: '🏃', name: 'Marathon Session', description: 'Practice for 2+ hours in one session', color: 'from-red-500 to-pink-600', category: 'Time Management' },
    
    // NEW: Achievement & Milestones
    'first-month': { emoji: '📆', name: 'First Month', description: 'Complete 30 days of practice', color: 'from-blue-500 to-indigo-600', category: 'Achievement & Milestones' },
    'half-year-hero': { emoji: '🎖️', name: 'Half Year Hero', description: 'Practice for 6 months', color: 'from-amber-500 to-yellow-600', category: 'Achievement & Milestones' },
    'year-of-music': { emoji: '🎊', name: 'Year of Music', description: 'Practice for a full year', color: 'from-purple-500 to-pink-600', category: 'Achievement & Milestones' },
    'century-club': { emoji: '💯', name: 'Century Club', description: '100 total practice sessions', color: 'from-emerald-500 to-green-600', category: 'Achievement & Milestones' },
    'thousand-club': { emoji: '🎯', name: 'Thousand Club', description: '1000 total practice sessions', color: 'from-violet-500 to-purple-600', category: 'Achievement & Milestones' },
    
    // NEW: Persistence & Recovery
    'comeback-kid': { emoji: '🔄', name: 'Comeback Kid', description: 'Return after a 7+ day break', color: 'from-orange-500 to-red-600', category: 'Persistence & Recovery' },
    
    // NEW: Teacher Assigned Badges
    'performance-ready': { emoji: '🎭', name: 'Performance Ready', description: 'Teacher says you\'re ready to perform!', color: 'from-rose-500 to-pink-600', category: 'Teacher Assigned' },
    'breakthrough-moment': { emoji: '💡', name: 'Breakthrough Moment', description: 'Teacher recognised you had a major breakthrough', color: 'from-yellow-400 to-orange-500', category: 'Teacher Assigned' },
    'technique-perfectionist': { emoji: '✨', name: 'Technique Perfectionist', description: 'Teacher praised your technique work', color: 'from-blue-400 to-indigo-500', category: 'Teacher Assigned' },
    'musical-expression': { emoji: '🎨', name: 'Musical Expression', description: 'Teacher loved your musical interpretation', color: 'from-purple-400 to-pink-500', category: 'Teacher Assigned' },
    'sight-reading-star': { emoji: '👁️', name: 'Sight Reading Star', description: 'Teacher impressed by your sight reading', color: 'from-green-400 to-teal-500', category: 'Teacher Assigned' },
    'memory-master': { emoji: '🧠', name: 'Memory Master', description: 'Teacher praised your memorisation skills', color: 'from-violet-400 to-purple-500', category: 'Teacher Assigned' },
    'rhythm-king': { emoji: '🥁', name: 'Rhythm King/Queen', description: 'Teacher loved your rhythm work', color: 'from-red-400 to-pink-500', category: 'Teacher Assigned' },
    'practice-champion': { emoji: '🏅', name: 'Practice Champion', description: 'Teacher noticed your excellent practice habits', color: 'from-amber-400 to-yellow-500', category: 'Teacher Assigned' },
    'creative-genius': { emoji: '🎪', name: 'Creative Genius', description: 'Teacher praised your creativity', color: 'from-pink-400 to-rose-500', category: 'Teacher Assigned' },
    'dedication-award': { emoji: '💎', name: 'Dedication Award', description: 'Teacher recognised your dedication', color: 'from-indigo-400 to-blue-500', category: 'Teacher Assigned' },
    'improvement-star': { emoji: '⭐', name: 'Improvement Star', description: 'Teacher noticed significant improvement', color: 'from-emerald-400 to-green-500', category: 'Teacher Assigned' },
    'lesson-hero': { emoji: '🦸', name: 'Lesson Hero', description: 'Teacher said you had an amazing lesson', color: 'from-orange-400 to-red-500', category: 'Teacher Assigned' },

    // String Technique Badges - Bow Technique
    'bow-hold-beginner': { emoji: '🤏', name: 'Bow Hold Beginner', description: 'You are learning a correct bow hold', color: 'from-amber-400 to-yellow-500', category: 'String Technique' },
    'bow-hold-developing': { emoji: '✊', name: 'Bow Hold Developing', description: 'You now have a stable bow hold', color: 'from-yellow-400 to-orange-500', category: 'String Technique' },
    'bow-hold-master': { emoji: '👑', name: 'Bow Hold Master', description: 'You consistently play with an excellent bow hold', color: 'from-orange-400 to-red-500', category: 'String Technique' },
    
    'straight-bow-beginner': { emoji: '📏', name: 'Straight Bow Beginner', description: 'You are learning to keep the bow straight', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'straight-bow-developing': { emoji: '📐', name: 'Straight Bow Developing', description: 'You now understand how to keep the bow straight', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },
    'straight-bow-champion': { emoji: '🏆', name: 'Straight Bow Champion', description: 'You consistently play with a straight bow', color: 'from-purple-400 to-pink-500', category: 'String Technique' },
    
    'spiccato-beginner': { emoji: '⚡', name: 'Spiccato Beginner', description: 'You are beginning to learn spiccato', color: 'from-green-400 to-teal-500', category: 'String Technique' },
    'spiccato-developing': { emoji: '💫', name: 'Spiccato Developing', description: 'You now understand how to bounce the bow clearly', color: 'from-teal-400 to-cyan-500', category: 'String Technique' },
    'spiccato-specialist': { emoji: '⭐', name: 'Spiccato Specialist', description: 'You consistently play controlled spiccato', color: 'from-cyan-400 to-blue-500', category: 'String Technique' },
    
    'staccato-beginner': { emoji: '🔸', name: 'Staccato Beginner', description: 'You are learning short, separated strokes', color: 'from-red-400 to-pink-500', category: 'String Technique' },
    'staccato-developing': { emoji: '🔹', name: 'Staccato Developing', description: 'You now understand how to play staccato cleanly', color: 'from-pink-400 to-rose-500', category: 'String Technique' },
    'staccato-star': { emoji: '✨', name: 'Staccato Star', description: 'You consistently play crisp staccato', color: 'from-rose-400 to-red-500', category: 'String Technique' },
    
    'legato-beginner': { emoji: '🌊', name: 'Legato Beginner', description: 'You are learning smooth connected bowing', color: 'from-violet-400 to-purple-500', category: 'String Technique' },
    'legato-developing': { emoji: '🎵', name: 'Legato Developing', description: 'You now have flowing legato phrases', color: 'from-purple-400 to-indigo-500', category: 'String Technique' },
    'legato-legend': { emoji: '🎼', name: 'Legato Legend', description: 'You consistently play beautiful legato', color: 'from-indigo-400 to-blue-500', category: 'String Technique' },
    
    'bow-distribution-beginner': { emoji: '⚖️', name: 'Bow Distribution Beginner', description: 'You are learning how to share bow evenly', color: 'from-emerald-400 to-green-500', category: 'String Technique' },
    'bow-distribution-developing': { emoji: '🎯', name: 'Bow Distribution Developing', description: 'You now understand bow planning', color: 'from-green-400 to-emerald-500', category: 'String Technique' },
    'bow-distribution-pro': { emoji: '🏅', name: 'Bow Distribution Pro', description: 'You consistently use bow distribution effectively', color: 'from-emerald-400 to-teal-500', category: 'String Technique' },
    
    'sautille-beginner': { emoji: '🦗', name: 'Sautillé Beginner', description: 'You are exploring Sautillé strokes', color: 'from-lime-400 to-green-500', category: 'String Technique' },
    'sautille-developing': { emoji: '🦎', name: 'Sautillé Developing', description: 'You now understand how to play Sautillé', color: 'from-green-400 to-emerald-500', category: 'String Technique' },
    'sautille-master': { emoji: '🦅', name: 'Sautillé Master', description: 'You consistently play controlled Sautillé', color: 'from-emerald-400 to-teal-500', category: 'String Technique' },

    // String Technique Badges - Left Hand Technique
    'vibrato-beginner': { emoji: '🌊', name: 'Vibrato Beginner', description: 'You are starting to explore vibrato', color: 'from-pink-400 to-rose-500', category: 'String Technique' },
    'vibrato-developing': { emoji: '💫', name: 'Vibrato Developing', description: 'You now have basic vibrato control', color: 'from-rose-400 to-red-500', category: 'String Technique' },
    'vibrato-virtuoso': { emoji: '🎭', name: 'Vibrato Virtuoso', description: 'You consistently play with beautiful vibrato', color: 'from-red-400 to-pink-500', category: 'String Technique' },
    
    'intonation-beginner': { emoji: '🎯', name: 'Intonation Beginner', description: 'You are learning to check finger accuracy', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'intonation-developing': { emoji: '📐', name: 'Intonation Developing', description: 'You now understand how to adjust intonation', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },
    'intonation-ace': { emoji: '🎪', name: 'Intonation Ace', description: 'You consistently play with accurate intonation', color: 'from-purple-400 to-pink-500', category: 'String Technique' },
    
    'shifting-beginner': { emoji: '🚀', name: 'Shifting Beginner', description: 'You are beginning to shift between positions', color: 'from-orange-400 to-red-500', category: 'String Technique' },
    'shifting-developing': { emoji: '🎢', name: 'Shifting Developing', description: 'You now have smoother position changes', color: 'from-red-400 to-pink-500', category: 'String Technique' },
    'shifting-star': { emoji: '⭐', name: 'Shifting Star', description: 'You consistently shift with accuracy and ease', color: 'from-pink-400 to-rose-500', category: 'String Technique' },
    
    'double-stops-beginner': { emoji: '🤝', name: 'Double Stops Beginner', description: 'You are learning to balance two notes', color: 'from-violet-400 to-purple-500', category: 'String Technique' },
    'double-stops-developing': { emoji: '⚖️', name: 'Double Stops Developing', description: 'You now understand how to tune double stops', color: 'from-purple-400 to-indigo-500', category: 'String Technique' },
    'double-stops-master': { emoji: '🎼', name: 'Double Stops Master', description: 'You consistently play clear, balanced double stops', color: 'from-indigo-400 to-blue-500', category: 'String Technique' },
    
    'trills-beginner': { emoji: '🦋', name: 'Trills Beginner', description: 'You are learning how to play trills', color: 'from-emerald-400 to-green-500', category: 'String Technique' },
    'trills-developing': { emoji: '🌿', name: 'Trills Developing', description: 'You now understand how to make trills even', color: 'from-green-400 to-emerald-500', category: 'String Technique' },
    'trills-talent': { emoji: '🎪', name: 'Trills Talent', description: 'You consistently play fast, clear trills', color: 'from-emerald-400 to-teal-500', category: 'String Technique' },

    // String Technique Badges - Advanced Techniques
    'harmonics-beginner': { emoji: '🔮', name: 'Harmonics Beginner', description: 'You are discovering natural harmonics', color: 'from-cyan-400 to-blue-500', category: 'String Technique' },
    'harmonics-developing': { emoji: '✨', name: 'Harmonics Developing', description: 'You now understand how to produce harmonics', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'harmonics-hero': { emoji: '🦸', name: 'Harmonics Hero', description: 'You consistently play clear harmonics', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },
    
    'pizzicato-beginner': { emoji: '👆', name: 'Pizzicato Beginner', description: 'You are learning how to pluck clearly', color: 'from-amber-400 to-yellow-500', category: 'String Technique' },
    'pizzicato-developing': { emoji: '🤏', name: 'Pizzicato Developing', description: 'You now understand how to project pizzicato', color: 'from-yellow-400 to-orange-500', category: 'String Technique' },
    'pizzicato-pro': { emoji: '💪', name: 'Pizzicato Pro', description: 'You consistently play strong, even pizzicato', color: 'from-orange-400 to-red-500', category: 'String Technique' },
    
    'col-legno-beginner': { emoji: '🪵', name: 'Col Legno Beginner', description: 'You are exploring col legno strokes', color: 'from-stone-400 to-gray-500', category: 'String Technique' },
    'col-legno-developing': { emoji: '🌳', name: 'Col Legno Developing', description: 'You now understand how to play col legno', color: 'from-gray-400 to-zinc-500', category: 'String Technique' },
    'col-legno-champion': { emoji: '🏆', name: 'Col Legno Champion', description: 'You consistently use col legno effectively', color: 'from-zinc-400 to-neutral-500', category: 'String Technique' },
    
    'sul-ponticello-beginner': { emoji: '🌊', name: 'Sul Ponticello Beginner', description: 'You are exploring sul ponticello sounds', color: 'from-sky-400 to-blue-500', category: 'String Technique' },
    'sul-ponticello-developing': { emoji: '🌀', name: 'Sul Ponticello Developing', description: 'You now understand how to control sul ponticello', color: 'from-blue-400 to-indigo-500', category: 'String Technique' },
    'sul-ponticello-star': { emoji: '💫', name: 'Sul Ponticello Star', description: 'You consistently create a strong sul ponticello sound', color: 'from-indigo-400 to-purple-500', category: 'String Technique' },

    // Universal Technique Badges - Musical Expression
    'dynamics-beginner': { emoji: '🔊', name: 'Dynamics Beginner', description: 'You are learning to play soft and loud with control', color: 'from-blue-400 to-indigo-500', category: 'Universal Technique' },
    'dynamics-developing': { emoji: '🎚️', name: 'Dynamics Developing', description: 'You now understand how to shape dynamics effectively', color: 'from-indigo-400 to-purple-500', category: 'Universal Technique' },
    'dynamics-expert': { emoji: '🎛️', name: 'Dynamics Expert', description: 'You consistently control dynamics with expression', color: 'from-purple-400 to-pink-500', category: 'Universal Technique' },
    
    'phrasing-beginner': { emoji: '🎵', name: 'Phrasing Beginner', description: 'You are learning to shape musical phrases', color: 'from-green-400 to-emerald-500', category: 'Universal Technique' },
    'phrasing-developing': { emoji: '🌊', name: 'Phrasing Developing', description: 'You now understand how to connect notes into flowing phrases', color: 'from-emerald-400 to-teal-500', category: 'Universal Technique' },
    'phrasing-pro': { emoji: '🎼', name: 'Phrasing Pro', description: 'You consistently play with expressive phrasing', color: 'from-teal-400 to-cyan-500', category: 'Universal Technique' },
    
    'articulation-beginner': { emoji: '🎯', name: 'Articulation Beginner', description: 'You are learning clear starts and stops', color: 'from-orange-400 to-red-500', category: 'Universal Technique' },
    'articulation-developing': { emoji: '🎪', name: 'Articulation Developing', description: 'You now understand how to vary articulation styles', color: 'from-red-400 to-pink-500', category: 'Universal Technique' },
    'articulation-ace': { emoji: '🏆', name: 'Articulation Ace', description: 'You consistently play with clear, accurate articulation', color: 'from-pink-400 to-rose-500', category: 'Universal Technique' },
    
    'tone-quality-beginner': { emoji: '🌟', name: 'Tone Quality Beginner', description: 'You are learning to produce a steady tone', color: 'from-yellow-400 to-orange-500', category: 'Universal Technique' },
    'tone-quality-developing': { emoji: '✨', name: 'Tone Quality Developing', description: 'You now understand how to refine and shape your tone', color: 'from-orange-400 to-red-500', category: 'Universal Technique' },
    'tone-quality-star': { emoji: '💫', name: 'Tone Quality Star', description: 'You consistently create a beautiful, resonant tone', color: 'from-red-400 to-pink-500', category: 'Universal Technique' },

    // Universal Technique Badges - Technical Foundation
    'posture-beginner': { emoji: '🧍', name: 'Posture Beginner', description: 'You are learning correct playing posture', color: 'from-emerald-400 to-green-500', category: 'Universal Technique' },
    'posture-developing': { emoji: '🚶', name: 'Posture Developing', description: 'You now understand how posture affects your playing', color: 'from-green-400 to-emerald-500', category: 'Universal Technique' },
    'posture-perfect': { emoji: '👑', name: 'Posture Perfect', description: 'You consistently maintain excellent posture', color: 'from-emerald-400 to-teal-500', category: 'Universal Technique' },
    
    'relaxation-beginner': { emoji: '🌱', name: 'Relaxation Beginner', description: 'You are learning to release tension while playing', color: 'from-lime-400 to-green-500', category: 'Universal Technique' },
    'relaxation-developing': { emoji: '🌿', name: 'Relaxation Developing', description: 'You now understand how to stay relaxed under pressure', color: 'from-green-400 to-emerald-500', category: 'Universal Technique' },
    'relaxation-mastery': { emoji: '🕊️', name: 'Relaxation Mastery', description: 'You consistently play with freedom and no excess tension', color: 'from-emerald-400 to-teal-500', category: 'Universal Technique' },
    
    'endurance-beginner': { emoji: '⏱️', name: 'Endurance Beginner', description: 'You are learning to maintain focus in practice', color: 'from-violet-400 to-purple-500', category: 'Universal Technique' },
    'endurance-developing': { emoji: '💪', name: 'Endurance Developing', description: 'You now understand how to build stamina in longer sessions', color: 'from-purple-400 to-indigo-500', category: 'Universal Technique' },
    'endurance-champion': { emoji: '🏅', name: 'Endurance Champion', description: 'You consistently sustain energy and concentration in practice', color: 'from-indigo-400 to-blue-500', category: 'Universal Technique' },

    // Special Occasion Badges
    'birthday-celebration': { emoji: '🎂', name: 'Birthday Practice Star', description: 'You practised on your birthday! What dedication!', color: 'from-pink-400 to-purple-600', category: 'Special Occasion' }
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    loadBadges();
  }, [currentUser, navigate]);

  const loadBadges = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      const allBadges = await loadAllUserBadges(currentUser.uid, db);
      
      // Check for new badges
      const storedBadges = localStorage.getItem(`badges_${currentUser.uid}`);
      const previousBadges = storedBadges ? JSON.parse(storedBadges) : [];
      
      // Check if we've recently celebrated badges (prevent duplicate celebrations)
      const lastCelebrationTime = localStorage.getItem(`lastBadgeCelebration_${currentUser.uid}`);
      const now = Date.now();
      const timeSinceLastCelebration = lastCelebrationTime ? now - parseInt(lastCelebrationTime) : Infinity;
      
      // Find new badges (both practice-earned and teacher-assigned)
      const newBadges = allBadges.filter((badgeId: string) => !previousBadges.includes(badgeId));
      
      // Only celebrate if we have new badges and haven't celebrated recently
      const shouldCelebrate = newBadges.length > 0 && timeSinceLastCelebration > 5 * 60 * 1000;
      
      if (newBadges.length > 0) {
        console.log('New badges detected:', newBadges);
        
        if (shouldCelebrate) {
          // Show celebration for the first new badge
          const firstNewBadge = newBadges[0];
          const badgeDef = badgeDefinitions[firstNewBadge];
          if (badgeDef) {
            setNewBadge({
              id: firstNewBadge,
              name: badgeDef.name,
              description: badgeDef.description,
              emoji: badgeDef.emoji,
              color: badgeDef.color
            });
            setShowBadgeCelebration(true);
            
            // Play celebration sound
            try {
              const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7blmGgU7k9n1unEiBC13yO/eizEIHWq+8+OWT');
              audio.play().catch(() => {}); // Ignore errors if audio fails
            } catch (error) {
              // Ignore audio errors
            }
            
            // Record the celebration time to prevent duplicates
            localStorage.setItem(`lastBadgeCelebration_${currentUser.uid}`, now.toString());
          }
        } else {
          console.log('Skipping celebration - too soon since last celebration');
        }
      }
      
      // Store current badges for future comparison
      localStorage.setItem(`badges_${currentUser.uid}`, JSON.stringify(allBadges));
      
      setEarnedBadges(allBadges);
      setBadgesLoaded(true);

      // Check for new birthday badge and show special celebration
      const savedBadges = JSON.parse(localStorage.getItem(`badges_${currentUser.uid}`) || '[]');
      if (!savedBadges.includes('birthday-celebration') && allBadges.includes('birthday-celebration')) {
        setShowBirthdayCelebration(true);
      }
    } catch (error) {
      console.error('Error loading badges:', error);
      setError('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };



  const renderBadge = (badgeId: string, isEarned: boolean) => {
    const badge = badgeDefinitions[badgeId];
    if (!badge) return null;

    return (
      <div
        key={badgeId}
        className={`${isEarned ? 'bg-gradient-to-br' : 'bg-gray-200'} ${badge.color} ${isEarned ? 'text-gray-900' : 'text-gray-800'} p-2 sm:p-4 rounded-lg sm:rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300 cursor-pointer group relative ${
          isEarned ? 'opacity-100' : 'opacity-70'
        }`}
        title={`${badge.name}: ${badge.description}`}
      >
        <div className={`text-center ${isEarned ? 'text-2xl sm:text-4xl md:text-5xl' : 'text-xl sm:text-3xl md:text-4xl'} mb-1 sm:mb-2`}>
          {badge.emoji}
        </div>
        <div className={`font-bold ${isEarned ? 'text-sm sm:text-lg md:text-xl' : 'text-xs sm:text-base md:text-lg'}`}>
          {badge.name}
        </div>
        <div className={`${isEarned ? 'text-xs sm:text-sm md:text-base' : 'text-xs sm:text-sm'} ${isEarned ? 'text-gray-800' : 'text-gray-700'} mt-1`}>
          {badge.description}
        </div>
        
        {/* Teacher Assigned tag for String Technique and Universal Technique badges */}
        {(badge.category === 'String Technique' || badge.category === 'Universal Technique') && (
          <div className="mt-2">
            <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full font-medium">
              Teacher Assigned
            </span>
          </div>
        )}
        
        {/* Earned indicator */}
        {isEarned && (
          <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )}
        
        {/* Tooltip */}
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10">
          <div className="font-semibold">{badge.name}</div>
          <div className="text-gray-300">{badge.description}</div>
        </div>
      </div>
    );
  };

  const getUnearnedBadges = () => {
    return Object.keys(badgeDefinitions).filter(badgeId => !earnedBadges.includes(badgeId));
  };

  const filterBadgesBySearch = (badgeIds: string[]) => {
    if (!searchTerm.trim()) return badgeIds;
    
    const searchLower = searchTerm.toLowerCase();
    return badgeIds.filter(badgeId => {
      const badge = badgeDefinitions[badgeId];
      if (!badge) return false;
      
      return badge.name.toLowerCase().includes(searchLower) ||
             badge.description.toLowerCase().includes(searchLower) ||
             badge.emoji.includes(searchLower);
    });
  };

  const isStringInstrumentPlayer = () => {
    if (!currentUser || !currentUser.instrument) return false;
    const stringInstruments = ['Violin', 'Viola', 'Cello', 'Double Bass'];
    return stringInstruments.includes(currentUser.instrument);
  };


  const getBadgesByCategory = (badgeIds: string[]) => {
    const categories: Record<string, string[]> = {};
    
    badgeIds.forEach(badgeId => {
      const category = badgeDefinitions[badgeId]?.category || 'Other';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(badgeId);
    });
    
    // Define the desired order for categories
    const categoryOrder = [
      'Streak',
      'Weekly Goals', 
      'Time Milestones',
      'Consistency',
      'Learning & Growth',
      'Time Management',
      'Achievement & Milestones',
      'Persistence & Recovery',
      'Special Occasion',
      'String Technique',
      'Universal Technique',
      'Teacher Assigned'
    ];
    
    // Create ordered categories object
    const orderedCategories: Record<string, string[]> = {};
    categoryOrder.forEach(category => {
      if (categories[category]) {
        orderedCategories[category] = categories[category];
      }
    });
    
    // Add any remaining categories not in the order list
    Object.keys(categories).forEach(category => {
      if (!categoryOrder.includes(category)) {
        orderedCategories[category] = categories[category];
      }
    });
    
    return orderedCategories;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading awards...</p>
          <p className="text-sm text-gray-500 mt-2">If this takes too long, check the console for errors</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-lg sm:text-3xl font-bold text-gray-900">🏆 Awards & Badges</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-medium transition-all duration-200"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {/* Earned Badges Section */}
          <div className="mb-12">
            <div className="text-center mb-8">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                🎉 Your Achievements
              </h2>
              <p className="text-gray-600">
                {earnedBadges.length} of {Object.keys(badgeDefinitions).length} badges earned
              </p>
            </div>

            {earnedBadges.length > 0 ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6">
                {earnedBadges.map(badgeId => renderBadge(badgeId, true))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Start Your Journey!</h3>
                <p className="text-gray-600">Complete your first practice session to earn your first badge.</p>
              </div>
            )}
          </div>

          {/* Unearned Badges Section */}
          {getUnearnedBadges().length > 0 && (
            <div>
              <div className="text-center mb-8">
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  🎯 Available Badges
                </h2>
                <p className="text-gray-600 mb-4">
                  Keep practicing to unlock these achievements! Most badges are earned automatically through your practice sessions, 
                  while technique badges are awarded by your teacher during lessons.
                  <span className="block mt-2 text-sm text-blue-600">
                    🎼 Universal technique badges are available for all instruments!
                  </span>
                  {isStringInstrumentPlayer() && (
                    <span className="block mt-1 text-sm text-purple-600">
                      🎻 String technique badges are also available for your instrument!
                    </span>
                  )}
                </p>
                
                {/* Search Bar */}
                <div className="max-w-md mx-auto">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search badges..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {Object.entries(getBadgesByCategory(filterBadgesBySearch(getUnearnedBadges().filter(badgeId => {
                const category = badgeDefinitions[badgeId]?.category;
                if (category === 'Teacher Assigned') return false;
                if (category === 'String Technique') return isStringInstrumentPlayer();
                if (category === 'Universal Technique') return true; // Available to all instruments
                return true;
              })))).map(([category, badgeIds]) => (
                <div key={category} className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">
                      {category === 'Streak' && '🔥'}
                      {category === 'Weekly Goals' && '📅'}
                      {category === 'Time Milestones' && '⏰'}
                      {category === 'Consistency' && '🎯'}
                      {category === 'Learning & Growth' && '📚'}
                      {category === 'Time Management' && '⏰'}
                      {category === 'Achievement & Milestones' && '🏆'}
                      {category === 'Persistence & Recovery' && '💪'}
                      {category === 'Special Occasion' && '🎂'}
                      {category === 'String Technique' && '🎻'}
                      {category === 'Universal Technique' && '🎼'}
                    </span>
                    {category}
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                    {badgeIds.map(badgeId => renderBadge(badgeId, false))}
                  </div>
                </div>
              ))}

            </div>
          )}

          {/* Progress Summary */}
          <div className="mt-12 bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Progress</h3>
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-indigo-600">{earnedBadges.length}</div>
                <div className="text-xs sm:text-sm text-gray-600">Badges Earned</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-green-600">
                  {Math.round((earnedBadges.length / Object.keys(badgeDefinitions).length) * 100)}%
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Completion Rate</div>
              </div>
              <div className="text-center">
                <div className="text-lg sm:text-2xl font-bold text-purple-600">
                  {Object.keys(badgeDefinitions).length - earnedBadges.length}
                </div>
                <div className="text-xs sm:text-sm text-gray-600">Badges Remaining</div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Badge Celebration Popup */}
      {showBadgeCelebration && newBadge && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform hover:scale-105 transition-transform duration-300 animate-pulse">
            <div className={`bg-gradient-to-br ${newBadge.color} p-8 rounded-t-2xl text-center`}>
              <div className="text-8xl mb-4 animate-pulse">
                {newBadge.emoji}
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                {newBadge.name}
              </h2>
              <p className="text-lg text-gray-800 mb-6">
                {newBadge.description}
              </p>
            </div>
            
            <div className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                🎉 Congratulations! 🎉
              </h3>
              <p className="text-lg text-gray-700 mb-6">
                Your teacher has awarded you a new badge for your amazing work!
              </p>
              
              <button
                onClick={() => setShowBadgeCelebration(false)}
                className={`bg-gradient-to-r ${newBadge.color} text-white px-8 py-4 rounded-xl font-bold text-lg hover:scale-105 transform transition-all duration-200 shadow-lg`}
              >
                {newBadge.id === 'birthday-celebration' 
                  ? birthdayMessages[Math.floor(Math.random() * birthdayMessages.length)]
                  : celebrationMessages[Math.floor(Math.random() * celebrationMessages.length)]
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Birthday Celebration Popup */}
      <BirthdayCelebration
        isOpen={showBirthdayCelebration}
        onClose={() => setShowBirthdayCelebration(false)}
        isBadgeEarned={true} // In Awards.tsx, this only shows when badge is already earned
      />
    </div>
  );
};

export default Awards;
